import { FC } from "react";
import { Fragment } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { MdArrowBackIos } from "react-icons/md";
import { defaultRenderIcon, StepperProps } from "../constants";
import useStepper from "../useStepper";
import cn from "classnames";

const StepperWeb: FC<StepperProps> = ({
  steps = [],
  disableNav,
  currentStep = 0,
  onStepChange,
  direction = "horizontal",
  connectorOverlays = [],
  className,
  classNames = {},
  getStepStatus,
  renderStepIcon,
  onBack,
  backLabel,
  onBackNode,
}) => {
  const { t } = useTranslation();
  const isVertical = direction === "vertical";
  const { getStatus, handleStepClick } = useStepper({ steps, currentStep, onStepChange, getStepStatus, disableNav });

  const handleClick = (index: number) => () => handleStepClick(index, getStatus(index));

  const stepsNode = (
    <div
      className={cn(
        "flex w-full",
        isVertical ? "flex-col items-start gap-4" : "items-center",
        classNames.root,
        className
      )}
    >
      {steps.map((step, index) => {
        const status = getStatus(index);
        const isFirst = index === 0;
        const isLast = index === steps.length - 1;

        return (
          <Fragment key={index}>
            <div
              data-status={status}
              data-first={isFirst ? "" : undefined}
              data-last={isLast ? "" : undefined}
              className={cn(
                "shrink-0",
                status === "disabled"
                  ? "cursor-not-allowed"
                  : "cursor-pointer",
                classNames.step
              )}
              onClick={handleClick(index)}
            >
              <div
                data-status={status}
                className={cn("flex items-center gap-2", classNames.item)}
              >
                <div data-status={status} className={cn(classNames.icon)}>
                  {renderStepIcon
                    ? renderStepIcon({
                        index,
                        status,
                        isActive: index === currentStep,
                      })
                    : defaultRenderIcon({ index, status })}
                </div>

                {step.label && (
                  <div
                    data-status={status}
                    className={cn("text-sm", classNames.label)}
                  >
                    {step.label}
                  </div>
                )}
              </div>
            </div>

            {index < steps.length - 1 && (
              <div
                className={cn(
                  "relative flex items-center",
                  isVertical ? "ms-3 h-10" : "mx-2 flex-1",
                  classNames.connector?.wrapper
                )}
              >
                <div
                  data-status={status}
                  className={cn(
                    isVertical ? "h-full w-0.5" : "h-0.5 w-full",
                    classNames.connector?.rail
                  )}
                />

                {connectorOverlays[index] && (
                  <div
                    className={cn(
                      "absolute bg-white px-1 text-xs",
                      "start-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
                      classNames.connector?.overlay
                    )}
                  >
                    {connectorOverlays[index]}
                  </div>
                )}
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );

  if (!onBack && !onBackNode) return stepsNode;

  return (
    <div className="flex w-full items-center">
      <div className="flex justify-start px-4 flex-1">
        {onBackNode ?? (
          <button
            type="button"
            onClick={onBack}
            className="text-font-minimal hover:text-font-active flex shrink-0 items-center gap-1 text-sm font-semibold cursor-pointer"
          >
            <MdArrowBackIos className="rtl:-scale-x-100" />
            {backLabel ?? t("UserHistory.common.back")}
          </button>
        )}
      </div>
      <div className="flex-3">{stepsNode}</div>
      <div className="flex-1" />
    </div>
  );
};

export default StepperWeb;;